import React, { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router';

import { useFollowedUsers } from '@exp/redux/following';
import { usePreviewMode } from '@exp/components/previewMode';
import LoadingSpinner from '@exp/components/loadingSpinner/LoadingSpinner';

import { PLP_ALT_MODES } from '../common/constants';

import '../../store/alert.less';

const CartsContainer = React.lazy(() => import(/* webpackChunkName: "cart" */ '../../cart/cartsContainer'));
const ErrorContainer = React.lazy(() => import(/* webpackChunkName: "error" */ '../../main/errorContainer'));
const NativeOrderHistoryGuidelines = React.lazy(() => import(/* webpackChunkName: "orderHistory-native" */ '../../orderHistory/nativeOrderHistoryGuidelines'));
const OrderConfirmation = React.lazy(() => import(/* webpackChunkName: "orderConfirmation" */ '../../orderConfirmation/orderConfirmation'));
const OrderHistoryContainer = React.lazy(() => import(/* webpackChunkName: "orderHistory" */ '../../orderHistory/orderHistoryContainer'));
const ProductCategoryContainer = React.lazy(() => import(/* webpackChunkName: "productCategory" */ '../../productCategory/productCategoryContainer'));
const ProductContainer = React.lazy(() => import(/* webpackChunkName: "product" */ '../../product/productContainer'));
const ProductUngatedContainer = React.lazy(() => import(/* webpackChunkName: "productUngated" */ '../../productUngated/productUngatedContainer'));
const Punchout = React.lazy(() => import(/* webpackChunkName: "punchout" */ '../../store/components/punchout/punchoutContainer'));
const SingleCheckoutContainer = React.lazy(() => import(/* webpackChunkName: "checkout" */ '../../singleCheckout/singleCheckoutContainer'));
const TopProducts = React.lazy(() => import(/* webpackChunkName: "topProducts" */ '../../love/topProducts'));
const UpdatePaymentContainer = React.lazy(() => import(/* webpackChunkName: "updatePayment" */ '../../updatePayment/updatePaymentContainer'));

const UseFollowedUsers = () => {
  useFollowedUsers();
  return null;
};

const UsePreviewMode = () => {
  usePreviewMode();
  return null;
};

const SuspendedRoute = ({
  children,
}: React.PropsWithChildren) => (
  <Suspense fallback={<LoadingSpinner />}>
    {children}
  </Suspense>
);

const ProductCategoryRoute = () => {
  const { orgId } = useParams();
  return (
    <ProductCategoryContainer orgId={orgId} />
  );
};

const ProductTaxonomyRoute = () => {
  const { taxonomy } = useParams();
  return (
    <ProductCategoryContainer taxonomy={taxonomy} />
  );
};

const TopProductsRoute = () => {
  const { taxonomy } = useParams();
  return (
    <SuspendedRoute>
      <TopProducts taxonomy={taxonomy} />
    </SuspendedRoute>
  );
};

const Router = () => (
  <>
    {window.Exp?.user?.uuid ? (
      <>
        <UseFollowedUsers />
        <UsePreviewMode />
      </>
    ) : null}

    <BrowserRouter>
      <Routes>
        <Route
          path="/product/:orgProductName/:orgId"
          element={(
            <SuspendedRoute>
              {window?.Exp?.user?.uuid.length ? <ProductContainer /> : <ProductUngatedContainer />}
            </SuspendedRoute>
          )}
        />
        <Route path="/love/:taxonomy?" element={<TopProductsRoute />} />
        <Route path="/shop">
          <Route path="brand/:orgId/error" element={<SuspendedRoute><ErrorContainer /></SuspendedRoute>} />
          <Route path="brand/:orgId/:orgKey" element={<SuspendedRoute><ProductCategoryRoute /></SuspendedRoute>} />
          <Route path="browse/:taxonomy/*?" element={<SuspendedRoute><ProductTaxonomyRoute /></SuspendedRoute>} />
          <Route path="preview/:orgId/product" element={<SuspendedRoute><ProductUngatedContainer /></SuspendedRoute>} />
          <Route path="recommend/:orgId/product" element={<SuspendedRoute><ProductUngatedContainer /></SuspendedRoute>} />
          <Route path="outlet" element={<SuspendedRoute><ProductCategoryContainer mode={PLP_ALT_MODES.OUTLET.key} /></SuspendedRoute>} />
          <Route path="*?" element={<Navigate to="/shop/browse/all" replace />} />
        </Route>
        <Route path="/store">
          <Route path=":storeId/:campaignId/punchout" element={<SuspendedRoute><Punchout /></SuspendedRoute>} />
          <Route path=":storeId/punchout" element={<SuspendedRoute><Punchout /></SuspendedRoute>} />
        </Route>
        <Route path="/orders">
          <Route path=":orderId/guidelines/:guidelinesPart?" element={<SuspendedRoute><NativeOrderHistoryGuidelines /></SuspendedRoute>} />
          <Route path="history" element={<SuspendedRoute><OrderHistoryContainer /></SuspendedRoute>} />
          <Route path="*?" element={<Navigate to="/orders/history" replace />} />
        </Route>
        <Route path="/checkout">
          <Route path="updateOrder/:storeId/:orderId" element={<SuspendedRoute><UpdatePaymentContainer /></SuspendedRoute>} />
          <Route path=":cartSetKey" element={<SuspendedRoute><SingleCheckoutContainer /></SuspendedRoute>} />
        </Route>
        <Route path="/cart">
          <Route path="success" element={<SuspendedRoute><OrderConfirmation /></SuspendedRoute>} />
          <Route path=":orgId?/:storeId?" element={<SuspendedRoute><CartsContainer /></SuspendedRoute>} />
        </Route>
      </Routes>
    </BrowserRouter>
  </>
);

export default Router;
